import {Color, Icon} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  ChatDraftStatus,
  createChat,
  selectFilesForDraft,
  setDraftMessage,
  submitDraftMessage,
} from 'llm/chat-library/ChatActions';
import {openChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {getChatTypeForNewAgentChat} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';

import {StudyOverviewViewQuery} from './__generated/StudyOverviewViewQuery';
import GenerateStudyReportModal, {GenerateStudyReportResult} from './GenerateStudyReportModal';

type StudyNode = ExtractByTypename<StudyOverviewViewQuery['study'], 'StudyNode'>;

interface GenerateStudyReportButtonProps {
  study: Pick<StudyNode, 'id' | 'name' | 'humanId'>;
}

async function handleSubmit(studyId: string, result: GenerateStudyReportResult): Promise<void> {
  const chatType = getChatTypeForNewAgentChat(AgentType.DEEP_RESEARCH);
  const {chatSessionId} = await createChat(chatType);

  const draft = {
    status: ChatDraftStatus.DRAFT,
    message: {text: result.prompt, selectedFiles: []},
    settings: {agentType: AgentType.DEEP_RESEARCH, publicDataEnabled: false},
  };
  setDraftMessage(chatSessionId, draft);

  if (result.file) {
    await selectFilesForDraft(chatSessionId, [result.file]);
  }

  submitDraftMessage(chatSessionId, {createParams: {contextIds: [studyId]}});

  openChat(chatSessionId);
}

export default function GenerateStudyReportButton({study}: GenerateStudyReportButtonProps): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_STUDY_GENERATE_REPORT_BUTTON, {
      studyId: study.id,
    });
  }, [study.id]);

  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      size={ButtonSize.SMALL}
      icon={<Icon icon="sparkle" className={css(styles.sparkleIcon)} />}
      text="Generate report"
      className={css(styles.button)}
      disabled={isSubmitting}
      onClick={async () => {
        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_STUDY_GENERATE_REPORT_BUTTON, {
          studyId: study.id,
        });

        let lastResult: GenerateStudyReportResult | undefined;
        let retry = true;

        while (retry) {
          retry = false;
          const result = await runModal(GenerateStudyReportModal, {
            studyName: study.name,
            studyApiId: study.humanId,
            defaultPrompt: lastResult?.prompt,
            defaultFile: lastResult?.file ?? null,
          });

          if (!result) {
            return;
          }

          try {
            setIsSubmitting(true);
            await handleSubmit(study.id, result);
          } catch (e) {
            if (e instanceof Error) {
              lastResult = result;
              notifications.error('Failed to generate report. Please try again.');
              retry = true;
            } else {
              throw e;
            }
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
      dataTestAttrs={{
        component: 'GenerateStudyReportButton',
        element: 'button',
      }}
    />
  );
}

const styles = StyleSheet.create({
  button: {
    backgroundColor: Color.BLUE_100,
    border: 'none',
    color: Color.BRAND_ROYAL,
    ':hover': {
      backgroundColor: Color.BLUE_150,
    },
  },
  sparkleIcon: {
    color: Color.BRAND_ROYAL,
    flexShrink: 0,
    height: 14,
    width: 14,
  },
});
